/**
 * Where a generated document came from, shown on the document itself (VALE-928).
 *
 * A generated document is an ordinary matter file and sits in the list with
 * everything else, so this is the answer to the question the list cannot hold:
 * which precedent produced it, who it was addressed to, what it swears to, and
 * whether Vale has the copy the lawyer last edited in Word.
 *
 * **It carries no buttons, and that is the design.** It used to, and the drawer
 * then showed two action bars a few centimetres apart — the drawer's own
 * (Download, the row's kebab) and this block's (Regenerate, a second kebab,
 * Edit in Word) — which read as two objects on screen rather than one document
 * with a history. Provenance is a *property* of the document; acting on the
 * document is the header's job. So Edit in Word moved to the header bar
 * ({@link PrecedentDocumentActions}), Regenerate into the drawer's one `⋯`
 * beside the document's other occasional actions, and what is left here is a
 * statement.
 *
 * Renders nothing for a document Vale did not generate, which is nearly all of
 * them. That is why it looks the record up itself rather than being handed one:
 * the drawer that hosts it draws every document on the matter and must not grow
 * a precedent-shaped prop to do it.
 */

'use client';

import { useCallback, useState } from 'react';
import { SquarePen } from 'lucide-react';
import { toast } from '@/lib/toast';

import { Button } from '@/components/ui/button';
import { MissingFieldsBanner } from '@/components/documents/missing-fields-banner';
import { handleMutationError } from '@/lib/api/direct-client';
import { openDeferredWordTab } from '@/components/templates/deferred-word-tab';
import { WordFreshnessSegments } from '@/components/templates/word-freshness';
import { useWordFocusPull } from '@/lib/hooks/use-word-focus-pull';
import { wordInviteFailureToast } from '@/lib/services/templates';
import {
  useEditPrecedentDocumentInWord,
  usePrecedentDocuments,
  useRefreshPrecedentDocumentFromWord
} from '@/lib/services/precedent-documents';
import type { PrecedentDocument } from '@/lib/services/precedent-documents';

/**
 * The record behind the document open in the drawer, or `null`.
 *
 * Both halves of this file take it the same way, and they share a query key, so
 * the panel and the header actions cost one fetch between them rather than
 * being threaded through the generic drawer as a prop.
 */
export function usePrecedentRecord(
  matterId: number | undefined,
  fileId: number | undefined
): PrecedentDocument | null {
  const { data } = usePrecedentDocuments(matterId ?? 0, matterId !== undefined);
  if (fileId === undefined) return null;
  return (
    data?.documents.find((document) => document.docxFileId === fileId) ?? null
  );
}

export function PrecedentProvenancePanel({
  matterId,
  fileId,
  onRegenerate
}: {
  matterId: number;
  /** The document open in the drawer. Matched against the record's file. */
  fileId: number;
  /**
   * Regenerate this document — the same handler the drawer's menu item runs,
   * passed in rather than rebuilt so the two cannot differ on the deferral.
   *
   * The blank-fields advisory offers it, because recording the missing details
   * does not change a document that has already been rendered: regenerating is
   * the second half of that fix, and it was previously named nowhere.
   */
  onRegenerate?: () => void;
}) {
  const record = usePrecedentRecord(matterId, fileId);
  const refreshFromWord = useRefreshPrecedentDocumentFromWord(matterId);

  /**
   * The manual control behind the freshness line: the lawyer came back, read a
   * stale timestamp and wants to poke it.
   *
   * It speaks up when Word could not be reached. Pressing refresh and getting
   * the same silence as "nothing new" is exactly how a stale document reads as
   * a current one (VALE-258).
   */
  const handleManualRefresh = async () => {
    if (!record) return;
    const result = await refreshFromWord.mutateAsync(record.id);
    if (result.status === 'failed') {
      toast.warning("Couldn't reach Word just now", {
        description:
          'Anything you have saved since may not be here yet. Try again in a moment.'
      });
    }
  };

  if (!record) return null;

  // Only once Word is actually in the picture. An untouched document has
  // nothing to be fresh or stale about, and offering a refresh on it would be a
  // control that does nothing.
  const showWordFreshness =
    record.wordCheckoutOpen || Boolean(record.lastSyncedFromWordAt);
  const hasExhibits = record.exhibits.length > 0;
  const hasBlankFields = record.missingOptionalFields.length > 0;

  // Nothing left to say. The provenance sentence itself is the drawer's
  // subtitle now, so on a plain letter that generated cleanly and has never
  // been to Word this block would be an empty container under the grid.
  if (!showWordFreshness && !hasExhibits && !hasBlankFields) return null;

  return (
    <section className="mb-4">
      {hasExhibits && (
        <p className="text-muted-foreground text-12px">
          Exhibits{' '}
          {record.exhibits
            .map((exhibit) => `${exhibit.letter}: ${exhibit.description}`)
            .join(' · ')}
        </p>
      )}

      {showWordFreshness && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
          <WordFreshnessSegments
            syncedAt={record.lastSyncedFromWordAt}
            checkoutOpen={record.wordCheckoutOpen}
            onRefresh={() => void handleManualRefresh()}
            refreshing={refreshFromWord.isPending}
          />
        </div>
      )}

      {/* The durable half of what the generate toast used to say and then threw
          away — carrying the control that actually resolves it, since filling
          the details in leaves this render exactly as it was. */}
      <MissingFieldsBanner
        fields={record.missingOptionalFields}
        action={
          onRegenerate
            ? { label: 'Regenerate', onClick: onRegenerate }
            : undefined
        }
        className="mt-3"
      />
    </section>
  );
}

/**
 * Edit in Word, for the document drawer's own header action bar.
 *
 * It sits beside Download rather than in a bar of its own because it acts on
 * the same object Download acts on. **Regenerate is deliberately not here** —
 * it lives in the drawer's `⋯`, with the document's other occasional actions,
 * so the bar carries only what a lawyer reaches for every time.
 *
 * Renders nothing for a document Vale did not generate.
 */
export function PrecedentDocumentActions({
  matterId,
  fileId
}: {
  matterId: number;
  fileId: number;
}) {
  const record = usePrecedentRecord(matterId, fileId);
  const editInWord = useEditPrecedentDocumentInWord(matterId);
  const refreshFromWord = useRefreshPrecedentDocumentFromWord(matterId);
  // Armed only once Word has actually been launched from here, so opening and
  // closing the drawer never touches Graph.
  const [launchedWord, setLaunchedWord] = useState(false);

  const { mutate: pullFromWord } = refreshFromWord;
  const recordId = record?.id ?? null;

  // "I'm back from Word", so re-read rather than waiting on the webhook.
  const onFocusPull = useCallback(() => {
    if (recordId !== null) pullFromWord(recordId);
  }, [pullFromWord, recordId]);
  useWordFocusPull({ armed: launchedWord, onPull: onFocusPull });

  if (!record) return null;

  const handleEditInWord = async () => {
    // The tab is opened synchronously inside the click, before provisioning:
    // a popup opened after an await is blocked (LF-5362).
    const tab = openDeferredWordTab();
    setLaunchedWord(true);
    try {
      const result = await editInWord.mutateAsync(record.id);
      tab.resolve(result.webUrl);
      // The document opened but the per-user SharePoint grant did not land. Say
      // so, rather than leaving them on Microsoft's bare access-denied page with
      // a correlation id and no Vale-side explanation (VALE-115).
      if (result.inviteFailure) {
        const advisory = wordInviteFailureToast(result.inviteFailure);
        toast.warning(advisory.title, { description: advisory.description });
      }
    } catch (error) {
      tab.cancel();
      toast.error(handleMutationError(error));
    }
  };

  if (!record.wordEditingAvailable) return null;

  return (
    <Button
      gap="standard"
      variant="outline"
      size="xs"
      disabled={editInWord.isPending}
      onClick={() => void handleEditInWord()}
    >
      <SquarePen className="h-3.5 w-3.5" />
      Edit in Word
    </Button>
  );
}
